import { useCallback } from 'react';

import useLocalStore from 'src/store/useLocalStore';
import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { Api } from 'src/models/Api.model';
import { GalenErrorKeys } from 'src/models/GalenData/GalenError.model';
import { convertSiteData } from 'src/models/GalenData/helpers/convertSiteData';
import {
  convertPracticeSettings,
  convertUserSettings,
} from 'src/models/GalenData/helpers/saveSettings';
import { AxiosResponseWithRefreshToken } from 'src/models/GalenData/Request.model';
import { GalenDataUser } from 'src/models/GalenData/User.model';
import { isValidSiteSelection } from 'src/models/Site.model';

import useGetGalenErrorMsg from './useGetGalenErrorMsg';

const useSetCurrentUserInfo = () => {
  const {
    setAuth,
    setCurrentUser,
    setUserSettings,
    setPracticeSettings,
    setSites,
    resetToInitialState,
  } = useRemoteBoundStore();

  const { getErrorMessage } = useGetGalenErrorMsg();

  const setCurrentUserInfo = useCallback(
    async (res: AxiosResponseWithRefreshToken<GalenDataUser>) => {
      const authToken = res.headers.authorization;
      const refreshToken = res.headers['x-refresh-token'];

      setAuth({
        authInfo: { authToken, refreshToken },
      });

      const data = res.data;

      try {
        const practiceId = data.currentRole?.practice?.practiceId;

        if (!practiceId) {
          throw new Error(getErrorMessage(GalenErrorKeys.PRACTICE_ID_REQUIRED));
        }

        const [userSettings, practiceSettings, siteData] = await Promise.all([
          Api.remote.getUserSettings(data.userId),
          Api.remote.getPracticeSettings(practiceId),
          Api.remote.get_site_data(),
        ]);

        const userSettingsData = convertUserSettings(userSettings.data);
        const practiceSettingsData = convertPracticeSettings(
          practiceSettings.data,
        );
        const sites = convertSiteData(siteData.data.content);

        setUserSettings({ userSettings: userSettingsData });
        setPracticeSettings({ practiceSettings: practiceSettingsData });
        setCurrentUser({ currentUser: data });
        setSites(sites);

        const { selectedSiteId, clearSelectedSiteId } =
          useLocalStore.getState();
        if (!isValidSiteSelection(sites, selectedSiteId)) {
          clearSelectedSiteId();
        }
      } catch (error) {
        resetToInitialState();
        throw error;
      }
    },
    [
      getErrorMessage,
      setAuth,
      setUserSettings,
      setPracticeSettings,
      setCurrentUser,
      setSites,
      resetToInitialState,
    ],
  );

  return { setCurrentUserInfo };
};

export default useSetCurrentUserInfo;
